import { useId, useState } from "react";
import { FieldError, FieldLabel } from "../AdminForm.jsx";
import { PRODUCT_TYPE_LABELS } from "../../lib/productOrders.js";

/**
 * The request fields of a product order: client (create only), product type,
 * description, quantity and the requested delivery date. Used by the create
 * panel and by the awaiting-quote edit.
 *
 * It holds the typed text and nothing more. Normalising, limits and the
 * single quantity conversion are lib/productOrders.js (buildCreatePayload /
 * buildEditInput); the caller runs those and passes `errors` back down.
 *
 * `pending`  the request has been sent and its outcome is not yet known:
 *            every field is frozen and the button says so. The values shown
 *            are the caller's `initial`, which while pending is the exact
 *            payload that was sent.
 * `disabled` the form may not be used at all right now (another request is
 *            unresolved; the detail could not be reloaded).
 */
export function ProductOrderForm({ initial, onSubmit, pending = false, disabled = false, clients = [], errors = {}, mode = "create", submitLabel, onCancel }) {
  const [form, setForm] = useState(() => ({
    clientId: initial?.clientId ?? "",
    productType: initial?.productType ?? "print",
    description: initial?.description ?? "",
    quantity: initial?.quantity === undefined || initial?.quantity === null ? "" : String(initial.quantity),
    requestedDeliveryDate: initial?.requestedDeliveryDate ?? "",
  }));
  const ids = useId();
  const frozen = pending || disabled;
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const describedBy = (field) => (errors[field] ? `${ids}-${field}-error` : undefined);
  const input = "w-full px-3 rounded border border-border-default text-[13px] text-text-primary bg-surface focus:outline-none focus:border-accent transition-colors disabled:opacity-60";

  return (
    <form
      noValidate
      onSubmit={(e) => { e.preventDefault(); if (!frozen) onSubmit(form); }}
      className="space-y-3"
      aria-busy={pending || undefined}
    >
      {mode === "create" && (
        <div>
          <FieldLabel><span id={`${ids}-clientId-label`}>Client</span><span className="text-negative ml-0.5">*</span></FieldLabel>
          <select id={`${ids}-clientId`} aria-labelledby={`${ids}-clientId-label`} value={form.clientId} onChange={set("clientId")} disabled={frozen} required
            aria-invalid={errors.clientId ? true : undefined} aria-describedby={describedBy("clientId")} className={`${input} h-9`}>
            <option value="">Choose a client…</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {errors.clientId && <FieldError><span id={`${ids}-clientId-error`}>{errors.clientId}</span></FieldError>}
        </div>
      )}

      <fieldset disabled={frozen}>
        <legend className="block text-[10px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.5px" }}>Product<span className="text-negative ml-0.5">*</span></legend>
        <div className="flex flex-wrap gap-4" role="radiogroup" aria-describedby={describedBy("productType")}>
          {Object.entries(PRODUCT_TYPE_LABELS).map(([value, label]) => (
            <label key={value} className="inline-flex items-center gap-1.5 text-[13px] text-text-primary">
              <input type="radio" name={`${ids}-productType`} value={value} checked={form.productType === value} onChange={set("productType")} className="accent-[var(--color-accent)]" />
              {label}
            </label>
          ))}
        </div>
        {errors.productType && <FieldError><span id={`${ids}-productType-error`}>{errors.productType}</span></FieldError>}
      </fieldset>

      <div>
        <FieldLabel><span id={`${ids}-description-label`}>Description</span><span className="text-negative ml-0.5">*</span></FieldLabel>
        <textarea id={`${ids}-description`} aria-labelledby={`${ids}-description-label`} value={form.description} onChange={set("description")} disabled={frozen} required rows={4} maxLength={4000}
          aria-invalid={errors.description ? true : undefined} aria-describedby={errors.description ? `${ids}-description-error` : `${ids}-description-help`}
          className={`${input} py-2 leading-snug`} placeholder="What the client asked for — size, stock, colours, artwork, anything a quote or invoice needs" />
        {errors.description
          ? <FieldError><span id={`${ids}-description-error`}>{errors.description}</span></FieldError>
          : <div id={`${ids}-description-help`} className="text-[10px] text-text-muted mt-1">Specifications useful for quoting and invoicing. Up to 4,000 characters.</div>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <FieldLabel><span id={`${ids}-quantity-label`}>Quantity</span><span className="text-negative ml-0.5">*</span></FieldLabel>
          <input id={`${ids}-quantity`} aria-labelledby={`${ids}-quantity-label`} type="text" inputMode="numeric" value={form.quantity} onChange={set("quantity")} disabled={frozen} required
            aria-invalid={errors.quantity ? true : undefined} aria-describedby={describedBy("quantity")} className={`${input} h-9`} placeholder="500" />
          {errors.quantity && <FieldError><span id={`${ids}-quantity-error`}>{errors.quantity}</span></FieldError>}
        </div>
        <div>
          <FieldLabel><span id={`${ids}-requestedDeliveryDate-label`}>Requested delivery</span></FieldLabel>
          <input id={`${ids}-requestedDeliveryDate`} aria-labelledby={`${ids}-requestedDeliveryDate-label`} type="date" value={form.requestedDeliveryDate} onChange={set("requestedDeliveryDate")} disabled={frozen}
            aria-invalid={errors.requestedDeliveryDate ? true : undefined} aria-describedby={errors.requestedDeliveryDate ? `${ids}-requestedDeliveryDate-error` : `${ids}-requestedDeliveryDate-help`} className={`${input} h-9`} />
          {errors.requestedDeliveryDate
            ? <FieldError><span id={`${ids}-requestedDeliveryDate-error`}>{errors.requestedDeliveryDate}</span></FieldError>
            : <div id={`${ids}-requestedDeliveryDate-help`} className="text-[10px] text-text-muted mt-1">The date the client asked for — not a promise. Leave blank if none was given.</div>}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        {onCancel && <button type="button" onClick={onCancel} disabled={pending} className="px-3 py-1.5 text-[13px] text-text-secondary disabled:opacity-50">Cancel</button>}
        <button type="submit" disabled={frozen} className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium disabled:opacity-50">
          {pending ? "Sending…" : submitLabel ?? (mode === "create" ? "Record order" : "Save changes")}
        </button>
      </div>
    </form>
  );
}
